import { useState } from 'react';

import { SERVER_ORIGIN } from '../config';

// Floating "💬" pill in the bottom-right corner that pops a modal where the
// user can leave a free-form report. Auto-captures small context (current
// tab, current run id, viewport size, user agent) so a report is actionable
// without back-and-forth. Anonymous-friendly: if there's no `token` it just
// POSTs without an Authorization header — server ties it to IP rate limit.

type Category = 'bug' | 'suggestion' | 'praise' | 'other';

interface FeedbackContext {
  tab?: string;       // 'lobby' | 'collection' | 'run' | 'account' | 'match'
  runId?: string;
  matchId?: string;
  url: string;
  userAgent: string;
  viewport: string;
}

export function FeedbackWidget({
  token, contextSnapshot,
}: {
  token: string | null;
  contextSnapshot: () => Partial<FeedbackContext>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Send feedback"
        style={{
          position: 'fixed', right: 16, bottom: 36, zIndex: 9000,
          background: '#1a1a24', color: '#ddd', border: '2px solid var(--accent-color)',
          borderRadius: 999, padding: '8px 14px', fontSize: 12,
          fontFamily: 'inherit', cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        }}
      >💬 Feedback</button>
      {open && (
        <FeedbackModal
          token={token}
          contextSnapshot={contextSnapshot}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function FeedbackModal({
  token, contextSnapshot, onClose,
}: {
  token: string | null;
  contextSnapshot: () => Partial<FeedbackContext>;
  onClose: () => void;
}) {
  const [category, setCategory] = useState<Category>('bug');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (body.trim().length === 0) { setError('Please write something.'); return; }
    setBusy(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const context: FeedbackContext = {
        ...contextSnapshot(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      };
      const res = await fetch(`${SERVER_ORIGIN}/auth/feedback`, {
        method: 'POST', headers,
        body: JSON.stringify({ category, body: body.trim(), context }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.error ?? 'Submission failed.');
        return;
      }
      setSent(true);
      // Auto-close after a moment so the success state is acknowledged.
      setTimeout(() => onClose(), 1500);
    } catch {
      setError('Network error.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0c0c16', border: '2px solid var(--accent-color)',
          borderRadius: 10, padding: 22, width: 460, maxWidth: '92vw',
          color: 'var(--text-color)',
        }}
      >
        <h2 style={{ margin: '0 0 4px', color: 'var(--accent-color)', fontSize: 18 }}>Feedback</h2>
        <p style={{ color: '#888', fontSize: 11, margin: '0 0 14px' }}>
          Bugs, suggestions, praise — anything. We read everything.
        </p>

        {sent ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ fontSize: 32 }}>✓</div>
            <div style={{ color: '#4CAF50', fontWeight: 'bold', marginTop: 4 }}>Thanks!</div>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['bug', 'suggestion', 'praise', 'other'] as const).map(c => {
                const active = category === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    style={{
                      flex: 1, padding: '6px 8px', fontSize: 11,
                      background: active ? 'var(--accent-color)' : '#15151f',
                      color: active ? '#000' : '#ccc',
                      border: `1px solid ${active ? 'var(--accent-color)' : '#333'}`,
                      borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
                      textTransform: 'capitalize', fontWeight: active ? 'bold' : 'normal',
                    }}
                  >{c}</button>
                );
              })}
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What happened? What were you trying to do?"
              autoFocus
              rows={6}
              disabled={busy}
              maxLength={4000}
              style={{
                padding: 10, fontSize: 13, fontFamily: 'inherit',
                background: '#15151f', border: '1px solid #333',
                borderRadius: 4, color: '#eee', resize: 'vertical', minHeight: 90,
              }}
            />
            <div style={{ color: '#666', fontSize: 10, textAlign: 'right' }}>
              {body.length} / 4000
            </div>
            {error && <div style={{ color: '#e57373', fontSize: 12 }}>{error}</div>}
            <div style={{ color: '#666', fontSize: 10, lineHeight: 1.4 }}>
              We auto-include your current screen, run id (if any), browser and viewport size to help debug — nothing more.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                style={{
                  background: 'none', border: '1px solid #555', color: '#ccc',
                  padding: '6px 14px', borderRadius: 4, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 12,
                }}
              >Cancel</button>
              <button
                type="submit"
                disabled={busy || body.trim().length === 0}
                className="multi-btn"
                style={{ fontSize: 12 }}
              >{busy ? 'Sending...' : 'Send'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
